import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Grid2 as Grid,
  Fade,
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../AuthContext'; // Assuming you have an AuthContext to get user data

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    '& svg': {
      transform: 'translateX(6px)',
    },
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1, 2),
  color: '#e74c3c',
  borderColor: '#e74c3c',
  '&:hover': {
    backgroundColor: '#e74c3c',
    color: 'white',
    borderColor: '#e74c3c',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

const Topics = () => {
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [schoolLevelFilter, setSchoolLevelFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user data from AuthContext

  const colorOptions = [
    { value: '#ffffff', label: 'White' },
    { value: '#f8d7da', label: 'Light Red' },
    { value: '#d4edda', label: 'Light Green' },
    { value: '#cce5ff', label: 'Light Blue' },
    { value: '#fff3cd', label: 'Light Yellow' },
    { value: '#e2e3e5', label: 'Light Gray' },
    { value: '#f5c6cb', label: 'Soft Pink' },
    { value: '#c3e6cb', label: 'Mint Green' },
    { value: '#b8daff', label: 'Sky Blue' },
    { value: '#ffeeba', label: 'Pale Yellow' },
    { value: '#d6bcfa', label: 'Lavender' },
    { value: '#fdd0e0', label: 'Baby Pink' },
    { value: '#b5f5e2', label: 'Aqua' },
    { value: '#fed7aa', label: 'Peach' },
    { value: '#e9d8fd', label: 'Lilac' },
    { value: '#a3bffa', label: 'Periwinkle' },
    { value: '#fed7d7', label: 'Coral' },
    { value: '#bef264', label: 'Lime' },
    { value: '#bbf7d0', label: 'Pistachio' },
    { value: '#bfdbfe', label: 'Cornflower Blue' },
  ];

  const accessStatusOptions = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const topicsCollection = collection(db, 'topics');
      const topicsSnapshot = await getDocs(query(topicsCollection));
      const topicsData = topicsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        accessStatus: doc.data().accessStatus || 'free', // Default to 'free' if not set
      }));

      const subjectsCollection = collection(db, 'subjects');
      const subjectsSnapshot = await getDocs(query(subjectsCollection));
      const subjectsData = subjectsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSubjects(subjectsData);

      const schoolLevelsCollection = collection(db, 'schoolLevels');
      const schoolLevelsSnapshot = await getDocs(query(schoolLevelsCollection));
      const schoolLevelsData = schoolLevelsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSchoolLevels(schoolLevelsData);

      setTopics(topicsData);
      setIsLoading(false);
    };

    fetchData().catch((error) => {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    });
  }, []);

  const filteredTopics = topics
    .filter((topic) => {
      const matchesText =
        topic.name.toString().toLowerCase().includes(searchText.toLowerCase()) ||
        topic.description.toString().toLowerCase().includes(searchText.toLowerCase());
      const matchesSubject = !subjectFilter || topic.subjectId === subjectFilter;
      const matchesSchoolLevel = !schoolLevelFilter || topic.schoolLevelId === schoolLevelFilter;

      return matchesText && matchesSubject && matchesSchoolLevel;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleEditClick = (topic) => {
    setSelectedTopic(topic);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTopic(null);
  };

  const handleSaveTopic = async () => {
    if (!selectedTopic) return;

    try {
      const topicRef = doc(db, 'topics', selectedTopic.id);
      await updateDoc(topicRef, {
        name: selectedTopic.name,
        description: selectedTopic.description,
        backgroundColor: selectedTopic.backgroundColor,
        subjectId: selectedTopic.subjectId,
        schoolLevelId: selectedTopic.schoolLevelId,
        accessStatus: selectedTopic.accessStatus,
      });

      setTopics(topics.map((topicItem) =>
        topicItem.id === selectedTopic.id ? selectedTopic : topicItem
      ));
      handleCloseModal();
    } catch (error) {
      console.error('Error updating topic:', error);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (window.confirm('Are you sure you want to delete this topic? This action cannot be undone.')) {
      try {
        const topicRef = doc(db, 'topics', topicId);
        await deleteDoc(topicRef);
        setTopics(topics.filter((topic) => topic.id !== topicId));
      } catch (error) {
        console.error('Error deleting topic:', error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedTopic((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const columns = [
    { field: 'name', headerName: 'Topic Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 400 },
    {
      field: 'subjectId',
      headerName: 'Subject',
      width: 150,
      renderCell: (params) => {
        const subject = subjects.find((s) => s.id === params.value);
        return subject ? subject.name : 'N/A';
      },
    },
    {
      field: 'schoolLevelId',
      headerName: 'School Level',
      width: 150,
      renderCell: (params) => {
        const level = schoolLevels.find((l) => l.id === params.value);
        return level ? level.name : 'N/A';
      },
    },
    {
      field: 'accessStatus',
      headerName: 'Access Status',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ color: params.value === 'premium' ? colors.accent : colors.primary, fontWeight: '600' }}>
          {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </AnimatedButton>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate(`/viewtopic/${params.row.id}`)}
          >
            View
          </AnimatedButton>
          <DeleteButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteTopic(params.row.id)}
          >
            Delete
          </DeleteButton>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <MenuBookIcon sx={{ fontSize: 35, mr: 1 }} />
            Topics
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Manage and edit topics in the portal.
          </Typography>
        </Box>

        {/* Filters and Add Button */}
        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                Topic List
              </Typography>
              <AnimatedButton
                variant="contained"
                color="primary"
                onClick={() => navigate('/addtopics')}
                startIcon={<AddIcon />}
              >
                Add New Topic
              </AnimatedButton>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Search Topics"
                  variant="outlined"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by name or description"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.secondary }}>Subject</InputLabel>
                  <Select
                    value={subjectFilter}
                    onChange={(e) => setSubjectFilter(e.target.value)}
                    label="Subject"
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    <MenuItem value=""><em>All Subjects</em></MenuItem>
                    {subjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.secondary }}>School Level</InputLabel>
                  <Select
                    value={schoolLevelFilter}
                    onChange={(e) => setSchoolLevelFilter(e.target.value)}
                    label="School Level"
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    <MenuItem value=""><em>All Levels</em></MenuItem>
                    {schoolLevels.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* DataGrid */}
            <Box sx={{ mt: 3 }}>
              <DataGrid
                rows={filteredTopics}
                columns={columns}
                pageSize={10}
                checkboxSelection={false}
                loading={isLoading}
                initialState={{
                  sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                }}
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { color: colors.secondary },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.background,
                    color: colors.secondary,
                    fontWeight: '600',
                  },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
                }}
              />
            </Box>
          </Box>
        </ModernCard>

        {/* Edit Modal */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <ModalBox sx={{padding:3, borderRadius: 5}}>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: 3 }}
            >
              Edit Topic
            </Typography>
            {selectedTopic && (
              <Box component="form">
                <TextField
                  fullWidth
                  label="Topic Name"
                  name="name"
                  value={selectedTopic.name}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedTopic.description}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Background Color</InputLabel>
                  <Select
                    name="backgroundColor"
                    value={selectedTopic.backgroundColor}
                    onChange={handleInputChange}
                  >
                    {colorOptions.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: color.value,
                              mr: 1,
                              border: '1px solid #ccc',
                            }}
                          />
                          {color.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subjectId"
                    value={selectedTopic.subjectId || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {subjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>School Level</InputLabel>
                  <Select
                    name="schoolLevelId"
                    value={selectedTopic.schoolLevelId || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {schoolLevels.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Access Status</InputLabel>
                  <Select
                    name="accessStatus"
                    value={selectedTopic.accessStatus || 'free'}
                    onChange={handleInputChange}
                  >
                    {accessStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <AnimatedButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveTopic}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </Box>
            )}
          </ModalBox>
        </Modal>
      </Container>
    </div>
  );
};

export default Topics;